import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Proposal } from '@shared/types';
import { getProposal } from '../api/client';
import { GridWorkspace } from '../components/GridWorkspace';
import { PlayIcon } from '../components/icons';
import { ProposalGallery } from '../components/ProposalGallery';
import { READ_ONLY_ACTIONS } from '../pages/types';
import { visibleSections } from '../lib/plan';

/**
 * /p/:id for a VIEW-ONLY teammate (docs/viewer-sharing.md). The owner's own
 * page grid and gallery/present mode (GridWorkspace + ProposalGallery), fed
 * the same full payload the owner's workspace gets (internal fields
 * included, unlike the client share link) — minus the agent bar, review
 * items and every edit control. The server refuses every write from a
 * viewer anyway; this view just never offers one.
 */
export function TeammateView({ proposalId, ownerUsername }: { proposalId: string; ownerUsername: string }): ReactElement {
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const gridScrollTop = useRef(0);

  useEffect(() => {
    let live = true;
    void getProposal(proposalId)
      .then((res) => {
        if (live) setProposal(res.proposal);
      })
      .catch((cause: unknown) => {
        if (live) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      live = false;
    };
  }, [proposalId, reloadKey]);

  useEffect(() => {
    if (!proposal) return;
    const name = proposal.advertiser.name;
    document.title = name ? `${name} — Media Proposal` : 'Media Proposal';
  }, [proposal]);

  if (error) return <p className="loading">{error}</p>;
  if (!proposal) return <p className="loading">Loading proposal…</p>;

  const firstPageId = visibleSections(proposal)[0]?.id ?? null;
  const name = proposal.opportunity.proposalName ?? proposal.advertiser.name ?? 'Untitled proposal';
  const pageCount = proposal.document?.sections.length ?? 0;
  const meta = `${pageCount} pages · v${proposal.version}`;

  return (
    <div className="teammate-shell">
      <header className="teammate-bar">
        <div className="teammate-bar-title">
          <h1>{name}</h1>
          <p>
            View only · {ownerUsername}'s proposal · v{proposal.version}
          </p>
        </div>
        <div className="teammate-bar-actions">
          <button
            type="button"
            className="workspace-action-button"
            onClick={() => firstPageId && setFocusedId(firstPageId)}
            disabled={!firstPageId}
            title="Start presentation from slide 1"
            aria-label="Start presentation from slide 1"
          >
            <PlayIcon />
          </button>
          <button type="button" className="btn btn-quiet" onClick={() => setReloadKey((key) => key + 1)}>
            Refresh
          </button>
        </div>
      </header>
      {!proposal.document ? (
        <p className="loading">This proposal has no pages yet.</p>
      ) : (
        <GridWorkspace
          proposal={proposal}
          updatingIds={[]}
          highlightedIds={[]}
          newlyAddedIds={[]}
          buildingIds={[]}
          updating={false}
          meta={meta}
          initialScrollTop={gridScrollTop.current}
          onScrollPositionChange={(top) => {
            gridScrollTop.current = top;
          }}
          onFocus={setFocusedId}
        />
      )}
      {focusedId && proposal.document ? (
        <ProposalGallery
          proposal={proposal}
          actions={READ_ONLY_ACTIONS}
          readOnly
          initialSectionId={focusedId}
          onActiveSectionChange={setFocusedId}
          onClose={() => setFocusedId(null)}
        />
      ) : null}
    </div>
  );
}
